import { runRenameChatWorkflow } from "@/action-dialog";
import { restoreErrorMessage, useRestoreFlowSession } from "@/api/archived";
import {
  useDeleteFlowSession,
  useUpdateWorkspaceInboxItem,
} from "@/api/flow_sessions";
import { useWorkspaces } from "@/api/workspaces";
import { showMovedToTrashToast } from "@/components/ConfirmTrashDialog/movedToTrashToast";
import { SelectableItem } from "@/dnd/SelectableItem";
import { useOpenChat } from "@/hooks/useOpenChat";
import type { FileRecordActionHandlerDeps } from "@/object-actions/handlers/fileRecord";
import type { ObjectActionHandlers } from "@/object-actions/types";
import type { FileRecord, InboxItem } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { ChatCardContainer } from "../../components/ChatCardContainer";
import { useCanvasCardActions } from "../../hooks/useCanvasCardActions";
import { useFileRecordCardActions } from "../../hooks/useFileRecordCardActions";
import { useWorkspaceSelectionOptional } from "../../hooks/useWorkspaceSelection";
import { handleWorkspaceItemActivate } from "../../workspaceItemActivate";
import { browserItemWorkspaceId } from "../utils/adapters";
import {
  browserItemId,
  browserItemSelectionId,
  type BrowserItem,
} from "../utils/homeBrowserTypes";
import {
  CanvasBrowserListRow,
  ChatBrowserListRow,
  FileBrowserListRow,
} from "./BrowserListRowContainers";

export interface UseBrowserItemRenderersParams {
  /** Scoped workspace; undefined on org-wide home. */
  workspaceId?: string;
  /** Every kind-specific card/row action needs the visible sets for
   *  permission checks and unique-name generation. */
  filesInScope: FileRecord[];
  /** Participate in the page selection model (select on single click,
   *  open on double). */
  selectable?: boolean;
  /** Register cards as drag sources. Only useful where a workspace-detail
   *  drop context is registered; elsewhere drags no-op. */
  draggable?: boolean;
  /** Folder-card open. The home browser filters folders out and stubs
   *  this; the workspace browser drills in. */
  onFolderOpen?: (record: FileRecord) => void;
  /** Create a canvas inside a folder card's folder. */
  folderExtraHandlers?: (record: FileRecord) => ObjectActionHandlers;
}

/**
 * Per-kind action wiring + grid/list renderers shared by the home browser
 * and the workspace browser. Owns the canvas/file card-action hooks and the
 * chat handlers; the browsers own tabs, data, and layout.
 */
export function useBrowserItemRenderers({
  workspaceId,
  filesInScope,
  selectable = true,
  draggable = false,
  onFolderOpen,
  folderExtraHandlers,
}: UseBrowserItemRenderersParams) {
  // Floating-vs-tab policy for chat rows (aliased: the local `openChat`
  // callback below owns the read-state bookkeeping around it).
  const { openChat: openChatPolicy } = useOpenChat();
  const queryClient = useQueryClient();
  const selectionCtx = useWorkspaceSelectionOptional();
  const selection = selectable ? selectionCtx : null;

  // "Shared with me" row indicator: the item lives in a workspace outside
  // the user's own list. Cached query — the rail already fetched it.
  const { data: workspaces } = useWorkspaces();
  const ownedWorkspaceIds = useMemo(
    () => new Set((workspaces ?? []).map((ws) => ws.id)),
    [workspaces],
  );
  const isSharedWithMe = useCallback(
    (item: BrowserItem) => {
      const id = browserItemWorkspaceId(item);
      // No workspace relation (skills) — not shared. Workspace list still
      // loading — don't mislabel an owned workspace as shared.
      if (!id || !workspaces) return false;
      return !ownedWorkspaceIds.has(id);
    },
    [workspaces, ownedWorkspaceIds],
  );

  // ----- canvas actions -----
  const { renderCanvasCard, actions: canvasActions } = useCanvasCardActions({
    selectable,
    draggable,
  });

  // ----- file actions -----
  const {
    renderFileCard,
    renderFolderCard,
    actions: fileActions,
  } = useFileRecordCardActions({
    workspaceId,
    allRecords: filesInScope,
    onFolderOpen: onFolderOpen ?? (() => {}),
    folderExtraHandlers,
  });

  /** Registry handler deps for the file list rows — the same wiring the
   *  grid's `WorkspaceFileCardItem` receives, so both views resolve the
   *  identical manifest menu. */
  const fileHandlerDeps = useMemo<FileRecordActionHandlerDeps>(
    () => ({
      onOpen: fileActions.openFile,
      onRename: fileActions.rename,
      onCopy: fileActions.copy,
      onDelete: fileActions.delete,
    }),
    [fileActions],
  );

  // ----- chat actions (Index page patterns) -----
  const updateInboxItem = useUpdateWorkspaceInboxItem();
  const deleteChatMutation = useDeleteFlowSession({
    onSuccess: () => {
      // Scope may be recents, starred, or any workspace list.
      queryClient.invalidateQueries({ queryKey: ["workspace-flow-sessions"] });
    },
    onError: () => toast.error("Failed to delete chat"),
  });
  const restoreChatMutation = useRestoreFlowSession({
    onError: (err) =>
      toast.error(restoreErrorMessage(err, "Failed to restore chat")),
  });

  const openChat = useCallback(
    (item: InboxItem) => {
      if (!item.is_read) {
        updateInboxItem.mutate({ id: item.id, is_read: true });
      }
      openChatPolicy(item.flow_session_id, { source: "browser-row" });
    },
    [updateInboxItem, openChatPolicy],
  );
  const deleteChat = useCallback(
    (item: InboxItem) => {
      const flowSessionId = item.flow_session_id;
      deleteChatMutation.mutate(flowSessionId, {
        onSuccess: () =>
          showMovedToTrashToast(() =>
            restoreChatMutation.mutate(flowSessionId),
          ),
      });
    },
    [deleteChatMutation, restoreChatMutation],
  );
  const toggleChatStar = useCallback(
    (item: InboxItem) =>
      updateInboxItem.mutate({ id: item.id, is_starred: !item.is_starred }),
    [updateInboxItem],
  );
  const renameChat = useCallback((item: InboxItem) => {
    window.setTimeout(() => void runRenameChatWorkflow({ item }), 0);
  }, []);

  const renderGridItem = useCallback(
    (item: BrowserItem): React.ReactNode => {
      switch (item.kind) {
        case "canvas":
          return renderCanvasCard(item.data, 0);
        case "file":
          return item.data.path.endsWith("/")
            ? renderFolderCard(item.data, 0)
            : renderFileCard(item.data, 0);
        case "chat": {
          const selId = `chat:${item.data.id}`;
          const isSelected = selection?.isSelected(selId) ?? false;
          const card = (
            <ChatCardContainer
              item={item.data}
              isSelected={isSelected}
              onOpen={openChat}
              onDelete={deleteChat}
              onToggleStar={toggleChatStar}
              onRename={renameChat}
              onClick={
                selection
                  ? (e) =>
                      handleWorkspaceItemActivate({
                        isSelected,
                        e,
                        onOpen: () => openChat(item.data),
                        onSelect: () => selection.cardClick(selId, e),
                      })
                  : undefined
              }
            />
          );
          if (!selection) return card;
          return (
            <SelectableItem
              id={selId}
              kind="chat"
              selectedIds={selection.selectedIds}
              selectOnly={selection.selectOnly}
              draggable={draggable}
            >
              {card}
            </SelectableItem>
          );
        }
      }
    },
    [
      renderCanvasCard,
      renderFileCard,
      renderFolderCard,
      openChat,
      deleteChat,
      toggleChatStar,
      renameChat,
      selection,
      draggable,
    ],
  );

  /** Selection props for a list row. */
  const rowSelectionProps = useCallback(
    (item: BrowserItem) => {
      const selId = selection ? browserItemSelectionId(item) : null;
      if (!selection || !selId) return {};
      return {
        selectionId: selId,
        isSelected: selection.isSelected(selId),
        onSelect: (e: React.MouseEvent) => selection.rowClick(selId, e),
      };
    },
    [selection],
  );

  /** Wrap a selectable row in `SelectableItem` so list rows are drag
   *  sources exactly like the grid cards (grid wraps inside the card
   *  hooks). Also the row's sole `data-workspace-item` /
   *  `data-selection-id` stamp. */
  const wrapRow = useCallback(
    (item: BrowserItem, row: React.ReactNode): React.ReactNode => {
      const selId = selection ? browserItemSelectionId(item) : null;
      if (!selection || !selId) return row;
      return (
        <SelectableItem
          key={browserItemId(item)}
          id={selId}
          kind={item.kind}
          selectedIds={selection.selectedIds}
          selectOnly={selection.selectOnly}
          draggable={draggable}
        >
          {row}
        </SelectableItem>
      );
    },
    [selection, draggable],
  );

  const renderRow = useCallback(
    (item: BrowserItem): React.ReactNode => {
      const key = browserItemId(item);
      switch (item.kind) {
        case "canvas": {
          const canvas = item.data;
          return wrapRow(
            item,
            <CanvasBrowserListRow
              key={key}
              item={item}
              canvas={canvas}
              sharedWithMe={isSharedWithMe(item)}
              canShare={canvasActions.canShare(canvas)}
              canDelete={canvasActions.canDelete(canvas)}
              onOpen={() => canvasActions.open(canvas)}
              {...rowSelectionProps(item)}
            />,
          );
        }
        case "file": {
          const record = item.data;
          return wrapRow(
            item,
            <FileBrowserListRow
              key={key}
              item={item}
              record={record}
              sharedWithMe={isSharedWithMe(item)}
              onOpen={() => fileActions.openFile(record)}
              handlerDeps={fileHandlerDeps}
              {...rowSelectionProps(item)}
            />,
          );
        }
        case "chat": {
          const chat = item.data;
          return wrapRow(
            item,
            <ChatBrowserListRow
              key={key}
              item={item}
              chat={chat}
              sharedWithMe={isSharedWithMe(item)}
              onOpen={openChat}
              onDelete={deleteChat}
              onToggleStar={toggleChatStar}
              onRename={renameChat}
              {...rowSelectionProps(item)}
            />,
          );
        }
      }
    },
    [
      canvasActions,
      fileActions,
      fileHandlerDeps,
      isSharedWithMe,
      openChat,
      toggleChatStar,
      renameChat,
      deleteChat,
      rowSelectionProps,
      wrapRow,
    ],
  );

  return {
    renderGridItem,
    renderRow,
    renderFolderCard,
    canvasActions,
    fileActions,
    chatActions: { open: openChat, delete: deleteChat },
  };
}
